import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSyncStore } from "@/store/syncStore";
import { track } from "@/pwa/analytics";

const EASE = [0.22, 1, 0.36, 1] as const;

function HouseOfflineGlyph() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="text-white/75" aria-hidden>
      <path d="M12 26 28 13l16 13" />
      <path d="M16 24v17h24V24" />
      <path d="M24 41v-8h8v8" />
      {/* disconnected signal arcs with a slash */}
      <path d="M22 9a10 10 0 0 1 12 0" className="text-cad/70" />
      <path d="M25 13a5 5 0 0 1 6 0" className="text-cad/70" />
      <path d="M19 6l18 14" className="text-safety" />
    </svg>
  );
}

// Controlled offline experience (Section 8). Full-screen informational overlay shown when
// connectivity is lost. Does not pretend online intelligence features work offline.
export function OfflineFallback() {
  const state = useSyncStore((s) => s.state);
  const setState = useSyncStore((s) => s.setState);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const online = () => {
      setState("online");
      track("online_restored");
    };
    const offline = () => {
      setState("offline");
      track("offline_detected");
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, [setState]);

  const retry = async () => {
    setChecking(true);
    try {
      await fetch("/manifest.json", { cache: "no-store" });
      setState("online");
      track("online_restored");
    } catch {
      /* still offline */
    } finally {
      setChecking(false);
    }
  };

  return (
    <AnimatePresence>
      {state === "offline" && (
        <motion.div
          data-testid="offline-fallback"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-obsidian-deep px-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="flex max-w-md flex-col items-center gap-6 text-center"
          >
            <HouseOfflineGlyph />
            <div className="flex flex-col gap-2">
              <h1 data-testid="offline-headline" className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">You're currently offline</h1>
              <p className="font-sans text-sm leading-relaxed text-white/60">
                Your internet connection appears to be unavailable. Reconnect to continue using BuildEase's online construction intelligence features.
              </p>
            </div>
            <button
              type="button"
              data-testid="offline-retry"
              onClick={retry}
              disabled={checking}
              className="rounded-2xl bg-safety px-7 py-3.5 font-display text-sm font-semibold text-obsidian transition-opacity duration-200 hover:opacity-90 disabled:opacity-60"
            >
              {checking ? "Checking…" : "Retry Connection"}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
